import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  VerifiedUser as VerifiedUserIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  BugReport as BugReportIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Code as CodeIcon,
  Accessibility as AccessibilityIcon
} from '@mui/icons-material';
import DevSecOpsTabs from '../../components/devsecops/DevSecOpsTabs';
import { useNavigate } from 'react-router-dom';
import DevSecOpsComponentCard, { DevSecOpsComponentDto } from '../../components/devsecops/DevSecOpsComponentCard';
import api, { ApiResponse, authService, User } from '../../services/api';
import { devsecopsApi } from '../../services/devsecopsApi';

const DevSecOpsQuality: React.FC = () => {
  const navigate = useNavigate();
  const [components, setComponents] = useState<DevSecOpsComponentDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<User['role'] | undefined>(undefined);

  const handleBack = () => {
    navigate('/devsecops');
  };

  const qcpComponents = [
    {
      icon: <CodeIcon />,
      title: 'Code Quality Standards',
      items: [
        'Automated code review with SonarQube or equivalent',
        'Adherence to coding standards and style guides',
        'Minimum 80% code coverage for unit tests',
        'Zero critical or high-severity security vulnerabilities',
        'Peer review required for all code changes'
      ]
    },
    {
      icon: <BugReportIcon />,
      title: 'Testing Strategy',
      items: [
        'Unit testing with Jest/Mocha for all components',
        'Integration testing for API endpoints',
        'End-to-end testing with Playwright or Cypress',
        'Accessibility testing with jest-axe and manual validation',
        'Performance testing for critical user flows'
      ]
    },
    {
      icon: <SecurityIcon />,
      title: 'Security & Compliance',
      items: [
        'OWASP Top 10 vulnerability scanning',
        'Dependency vulnerability scanning (npm audit, Snyk)',
        'Section 508 compliance validation',
        'FISMA compliance for federal systems',
        'Secrets scanning and prevention (GitLeaks)'
      ]
    },
    {
      icon: <AccessibilityIcon />,
      title: 'Accessibility Requirements',
      items: [
        'WCAG 2.0 Level AA compliance minimum',
        'Section 508 standards adherence',
        'Keyboard navigation for all interactive elements',
        'Screen reader compatibility testing',
        'Color contrast ratios meeting 4.5:1 minimum'
      ]
    }
  ];

  const performanceMetrics = [
    { metric: 'Code Coverage', target: '≥ 80%', measurement: 'Automated via Jest/Istanbul' },
    { metric: 'Build Success Rate', target: '≥ 95%', measurement: 'CI/CD pipeline metrics' },
    { metric: 'Deployment Frequency', target: 'Multiple per day', measurement: 'Railway/Cloudflare deployment logs' },
    { metric: 'Mean Time to Recovery (MTTR)', target: '< 1 hour', measurement: 'Incident tracking system' },
    { metric: 'Change Failure Rate', target: '< 5%', measurement: 'Rollback frequency analysis' },
    { metric: 'Lead Time for Changes', target: '< 1 day', measurement: 'Commit to production time' },
    { metric: 'Security Scan Pass Rate', target: '100%', measurement: 'Automated security scans' },
    { metric: '508 Compliance Score', target: '100%', measurement: 'Automated + manual testing' }
  ];

  const cicdGates = [
    'All unit tests must pass',
    'Code coverage must meet or exceed 80%',
    'No critical or high-severity security vulnerabilities',
    'Accessibility tests must pass (jest-axe)',
    'Build must complete successfully',
    'Platform configuration validation must pass',
    'No secrets or sensitive data in code'
  ];

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        setLoading(true);
        const response = await devsecopsApi.get<ApiResponse<DevSecOpsComponentDto[]>>(
          '/devsecops/components',
          {
            params: { page: 'quality' }
          }
        );

        const data = response.data.data || [];
        setComponents(data);
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load quality assurance components';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchComponents();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        setCurrentUserRole(user.role);
      } catch (_err) {
        // unauthenticated users will see read-only cards
      }
    };

    loadUser();
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to DevSecOps Program"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Quality Assurance
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Quality Control Plan, Performance Requirements, and Testing Standards
          </Typography>
        </Box>
      </Box>

      {/* DevSecOps Tabs */}
      <DevSecOpsTabs activeTab="quality" />

      {/* Quality Assurance Capabilities (dynamic) */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
        Quality Assurance Capabilities
      </Typography>

      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading quality assurance components...
        </Typography>
      )}

      {error && (
        <Typography variant="body2" color="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {components.map((component: DevSecOpsComponentDto) => (
          <Grid item xs={12} md={6} key={component.key}>
            <DevSecOpsComponentCard
              component={component}
              currentUserRole={currentUserRole}
              onUpdated={(updated) => {
                setComponents((prev: DevSecOpsComponentDto[]) =>
                  prev.map((c: DevSecOpsComponentDto) => (c.key === updated.key ? updated : c))
                );
              }}
            />
          </Grid>
        ))}
      </Grid>

      {/* QCP Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <VerifiedUserIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
          <Typography variant="h5" color="primary" fontWeight="bold">
            Quality Control Plan (QCP)
          </Typography>
        </Box>
        <Typography variant="body1" paragraph>
          The Quality Control Plan establishes comprehensive quality assurance processes, testing strategies,
          and acceptance criteria for all DevSecOps deliverables. The QCP ensures that all work products meet
          federal standards for security, accessibility, and performance.
        </Typography>
        <Grid container spacing={3}>
          {qcpComponents.map((component, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card elevation={1} sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: 'primary.main', mr: 2 }}>
                      {component.icon}
                    </Box>
                    <Typography variant="h6" fontWeight="bold">
                      {component.title}
                    </Typography>
                  </Box>
                  <List dense>
                    {component.items.map((item, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        </CardContent>
      </Card>

      {/* Performance Requirements */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SpeedIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
          <Typography variant="h5" color="primary" fontWeight="bold">
            Performance Requirements & Metrics
          </Typography>
        </Box>
        <Typography variant="body1" paragraph>
          The following performance metrics are tracked and reported monthly to ensure continuous improvement
          and alignment with ODOS IV objectives:
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Metric</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Target</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Measurement Method</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {performanceMetrics.map((metric, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>{metric.metric}</TableCell>
                  <TableCell>
                    <Chip label={metric.target} size="small" color="primary" />
                  </TableCell>
                  <TableCell>{metric.measurement}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </CardContent>
      </Card>

      {/* CI/CD Quality Gates */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AssessmentIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
          <Typography variant="h5" color="primary" fontWeight="bold">
            CI/CD Quality Gates
          </Typography>
        </Box>
        <Typography variant="body1" paragraph>
          All code changes must pass the following automated quality gates before deployment to any environment:
        </Typography>
        <List>
          {cicdGates.map((gate, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText primary={gate} />
            </ListItem>
          ))}
        </List>
        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="text.secondary">
          <strong>Automated Enforcement:</strong> GitHub Actions CI/CD pipeline automatically enforces all quality
          gates. Failed checks block merging to main branch and prevent deployment to production environments.
          Manual overrides require approval from Tech Lead and documented justification.
        </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DevSecOpsQuality;
